/**
 * Card capture + identification flow.
 *
 * Self-contained full-screen modal. Hands off to the native iOS / Android
 * camera via `<input type="file" capture="environment">`, gets back a
 * full-resolution JPEG, resizes it client-side, POSTs to /api/price-lookup/scan,
 * then walks the user through candidate picker → fast-add (buy) or CardDetail
 * (sell).
 *
 * Why native camera and not getUserMedia: on iPhone, the live-video API
 * produces low-resolution frames with webcam-grade autofocus, no HDR, and
 * triggers iOS auto-macro mid-shot. The native camera uses the full photo
 * pipeline (12 MP, proper AF lock, lens selection, stabilisation), giving
 * Claude Vision the resolution it needs to read collector numbers and set
 * symbols.
 */

import { useCallback, useRef, useState } from 'react';
import { AlertCircle, Camera, RefreshCw, Search, X } from 'lucide-react';

import { scanCard, recordScanPick, ApiError, TimeoutError } from '../lib/api';
import {
  calculateBuyPrice,
  cn,
  formatPrice,
  getActivePrices,
  getEditionFullName,
} from '../lib/utils';
import { track } from '../lib/telemetry';
import type { Card, CartItem, Edition, Mode, PriceConditions } from '../lib/types';
import { CardImage } from './CardImage';
import { Button } from './ui/button';

type Phase = 'idle' | 'processing' | 'picker' | 'confirm' | 'empty' | 'error';

// Two scan intents fork the post-scan UI:
//   'add'   — buy-mode batch flow: ConfirmSheet → fast-add to cart
//   'quote' — show full price grid (CardDetail), no auto-commit. Useful when a
//             customer walks up asking "what'll you give me for this?".
//             Operator can still tap "Add to Cart" inside CardDetail if they decide.
type ScanIntent = 'add' | 'quote';

const HIGH_CONFIDENCE_THRESHOLD = 0.8;
// 1600 px max side — roughly matches Claude Vision's ~1568 optimal-detail band.
// Native iPhone photos are ~12 MP (~4032 × 3024); we downscale before upload to
// keep the payload under ~1 MB while preserving readable card text.
const RESIZE_MAX_SIDE = 1600;
const JPEG_QUALITY = 0.85;

const CONDITION_PILLS: Array<{ key: keyof PriceConditions; label: string }> = [
  { key: 'nm', label: 'NM' },
  { key: 'lp', label: 'LP' },
  { key: 'mp', label: 'MP' },
  { key: 'hp', label: 'HP' },
  { key: 'dm', label: 'DMG' },
];

interface CameraCaptureProps {
  mode: Mode;
  buyPercent: number;
  onClose: () => void;
  /** Sell-mode path: open the full CardDetail for the picked candidate. */
  onCardSelected: (card: Card) => void;
  /** Quote-only path: open CardDetail without the Add-to-Cart action. */
  onCardForQuote: (card: Card) => void;
  /** Buy-mode path: fast-add the confirmed line item to the cart. */
  onAddToCart: (item: Omit<CartItem, 'id'>) => void;
  /** No-candidate path: drop recognized text into the search bar. */
  onFallbackSearch: (query: string) => void;
}

export function CameraCapture({
  mode,
  buyPercent,
  onClose,
  onCardSelected,
  onCardForQuote,
  onAddToCart,
  onFallbackSearch,
}: CameraCaptureProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [phase, setPhase] = useState<Phase>('idle');
  const [scanIntent, setScanIntent] = useState<ScanIntent>('add');
  const [candidates, setCandidates] = useState<Card[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<Card | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<keyof PriceConditions>('nm');
  const [selectedEdition, setSelectedEdition] = useState<Edition | null>(null);
  const [fallbackQuery, setFallbackQuery] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Per-scan correlation ID returned by /api/price-lookup/scan. Used to update
  // the trace row's picked_card_id once the user picks/cancels.
  const [scanRequestId, setScanRequestId] = useState<string | null>(null);

  const handleTakePhotoForAdd = useCallback(() => {
    setScanIntent('add');
    track('scan_started', { intent: 'add', mode });
    fileInputRef.current?.click();
  }, [mode]);

  const handleTakePhotoForQuote = useCallback(() => {
    setScanIntent('quote');
    track('scan_started', { intent: 'quote', mode });
    fileInputRef.current?.click();
  }, [mode]);

  const handleClose = useCallback(() => {
    track('scan_cancelled', { phase });
    // If a scan completed and the user closed without picking, record the
    // cancel on the trace row so the dashboard can distinguish "user saw
    // candidates but rejected them all" from "scan never returned anything".
    if (scanRequestId) {
      void recordScanPick(scanRequestId, null, -1);
    }
    onClose();
  }, [onClose, phase, scanRequestId]);

  // Retake from an empty/error state — preserves the original scan intent
  // (quote retries stay quote, add retries stay add).
  const handleRetakePhoto = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset the input so picking the same file twice still fires onChange.
      event.target.value = '';
      if (!file) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      setPhase('processing');
      const t0 = Date.now();

      try {
        const blob = await resizeImageFile(file, canvas);
        const response = await scanCard(blob, { intent: scanIntent, mode });
        const latencyMs = Date.now() - t0;
        setScanRequestId(response.request_id ?? null);

        if (response.candidates.length === 0) {
          setFallbackQuery(response.fallback_query);
          setPhase('empty');
          track('scan_no_match', {
            confidence: response.raw_ocr.confidence,
            ocr_name: response.raw_ocr.name,
          ocr_number: response.raw_ocr.number,
          ocr_set_hint: response.raw_ocr.set_hint,
            fallback_query: response.fallback_query,
            latency_ms: latencyMs,
          });
          return;
        }

        track('scan_succeeded', {
          confidence: response.raw_ocr.confidence,
          candidate_count: response.candidates.length,
          ocr_name: response.raw_ocr.name,
          ocr_number: response.raw_ocr.number,
          ocr_set_hint: response.raw_ocr.set_hint,
          matched_tier: response.matched_tier,
          latency_ms: latencyMs,
        });

        // Only auto-advance when the strict tier (name + number + set) found
        // a single match. Fallback tiers (no_set, stripped, name_only) used
        // looser criteria than what Claude actually extracted, so a single
        // match there could be a coincidental same-name/same-number card from
        // a different set/era — we want operator confirmation in those cases.
        const isHighConfidenceSingle =
          response.candidates.length === 1 &&
          response.raw_ocr.confidence >= HIGH_CONFIDENCE_THRESHOLD &&
          response.matched_tier === 'strict';

        if (isHighConfidenceSingle) {
          const only = response.candidates[0];
          // Auto-advance skips the picker, so the user never explicitly taps a
          // candidate — but the trace row still needs the picked_card_id /
          // picked_index to mark this scan as "resolved". Record before the
          // branching below since startConfirm / onCardForQuote / onCardSelected
          // may unmount this component.
          if (response.request_id) {
            void recordScanPick(response.request_id, only.id, 0);
          }
          // Quote intent: open CardDetail without the Add-to-Cart action.
          // Sell mode: open standard CardDetail with Add-to-Cart available.
          // Buy + add intent: open the fast-add ConfirmSheet.
          if (scanIntent === 'quote') {
            onCardForQuote(only);
            return;
          }
          if (mode === 'sell') {
            onCardSelected(only);
            return;
          }
          startConfirm(only);
          return;
        }

        setCandidates(response.candidates);
        setPhase('picker');
      } catch (err) {
        // Surface raw error in the console for remote-debugging on mobile.
        console.error('[scan] capture failed:', err);
        setPhase('error');
        setErrorMessage(describeScanError(err));
        track('scan_failed', {
          error_class:
            err instanceof ApiError
              ? `api_${err.status}`
              : err instanceof TimeoutError
              ? 'timeout'
              : err?.constructor?.name || 'unknown',
          status: err instanceof ApiError ? err.status : null,
          message: err instanceof Error ? err.message : String(err),
          latency_ms: Date.now() - t0,
        });
      }
    },
    // scanIntent and onCardForQuote MUST be in deps — without them, the callback
    // closes over the initial scanIntent='add' value forever, so taps on the
    // "Quote price" button silently route through the buy/add path instead.
    // (startConfirm is intentionally omitted: it's declared below this callback,
    // referencing it here would TDZ-throw, and it has empty deps so it's stable.)
    [mode, scanIntent, onCardSelected, onCardForQuote]
  );

  const startConfirm = useCallback((card: Card) => {
    setSelectedCandidate(card);
    setSelectedCondition(defaultConditionForCard(card));
    setSelectedEdition(null);
    setPhase('confirm');
  }, []);

  const handleCandidatePick = useCallback(
    (card: Card) => {
      const index = candidates.findIndex((c) => c.id === card.id);
      track('scan_candidate_picked', {
        card_id: card.id,
        card_name: card.name,
        index,
        candidate_count: candidates.length,
      });
      // Finalize the trace row's outcome — server stores picked_card_id and
      // picked_index. Fire-and-forget; recordScanPick swallows failures so
      // a trace write hiccup never blocks the cart flow.
      if (scanRequestId) {
        void recordScanPick(scanRequestId, card.id, index);
      }
      // Mirror the auto-advance branching from handleFileChange.
      if (scanIntent === 'quote') {
        onCardForQuote(card);
        return;
      }
      if (mode === 'sell') {
        onCardSelected(card);
        return;
      }
      startConfirm(card);
    },
    [candidates, mode, scanIntent, onCardSelected, onCardForQuote, startConfirm, scanRequestId]
  );

  const handleAddClick = useCallback(() => {
    if (!selectedCandidate) return;
    if (mode === 'sell') {
      onCardSelected(selectedCandidate);
      return;
    }
    const prices = getActivePrices(selectedCandidate, selectedEdition);
    const raw = prices?.[selectedCondition];
    // Cards without price data (e.g. Japanese-only prints not on TCGplayer)
    // can still be added at $0 so the operator can set the offer manually
    // in the cart drawer. Don't block the add path on missing price data.
    const finalPrice = raw != null ? calculateBuyPrice(raw, buyPercent) : 0;
    onAddToCart({
      cardId: selectedCandidate.id,
      cardName: selectedCandidate.name,
      setName: selectedCandidate.set_name,
      cardNumber: selectedCandidate.card_number,
      condition: selectedCondition,
      originalPrice: finalPrice,
      finalPrice,
      edition: selectedEdition ?? undefined,
    });
    track('scan_added_to_cart', {
      card_id: selectedCandidate.id,
      card_name: selectedCandidate.name,
      condition: selectedCondition,
      edition: selectedEdition,
      final_price: finalPrice,
    });
  }, [
    mode,
    onCardSelected,
    onAddToCart,
    selectedCandidate,
    selectedCondition,
    selectedEdition,
    buyPercent,
  ]);

  const handleRetry = useCallback(() => {
    setErrorMessage(null);
    setCandidates([]);
    setSelectedCandidate(null);
    setSelectedEdition(null);
    setSelectedCondition('nm');
    setFallbackQuery(null);
    setPhase('idle');
  }, []);

  const handleFallbackTap = useCallback(() => {
    if (fallbackQuery) onFallbackSearch(fallbackQuery);
  }, [fallbackQuery, onFallbackSearch]);

  return (
    <div className="fixed inset-0 z-50 bg-background">
      {/* Hidden canvas for resize; hidden file input for native camera. */}
      <canvas ref={canvasRef} className="hidden" />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Close button — always accessible */}
      <button
        onClick={handleClose}
        className="absolute right-4 top-4 z-20 rounded-full bg-muted p-2 text-muted-foreground transition-colors hover:bg-outline/20 hover:text-foreground"
        aria-label="Close"
      >
        <X className="h-6 w-6" />
      </button>

      {phase === 'idle' && (
        <IdleView
          mode={mode}
          onTakePhotoForAdd={handleTakePhotoForAdd}
          onTakePhotoForQuote={handleTakePhotoForQuote}
        />
      )}

      {phase === 'processing' && <ProcessingView />}

      {phase === 'picker' && (
        <PickerView candidates={candidates} onPick={handleCandidatePick} />
      )}

      {phase === 'confirm' && selectedCandidate && (
        <ConfirmSheet
          card={selectedCandidate}
          mode={mode}
          buyPercent={buyPercent}
          selectedCondition={selectedCondition}
          onConditionChange={setSelectedCondition}
          selectedEdition={selectedEdition}
          onEditionChange={setSelectedEdition}
          onAdd={handleAddClick}
        />
      )}

      {phase === 'empty' && (
        <EmptyView
          fallbackQuery={fallbackQuery}
          onFallback={handleFallbackTap}
          onRetry={handleRetry}
          onTakePhoto={handleRetakePhoto}
        />
      )}

      {phase === 'error' && (
        <ErrorView
          message={errorMessage}
          onRetry={handleRetry}
          onTakePhoto={handleRetakePhoto}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase views
// ---------------------------------------------------------------------------

function IdleView({
  mode,
  onTakePhotoForAdd,
  onTakePhotoForQuote,
}: {
  mode: Mode;
  onTakePhotoForAdd: () => void;
  onTakePhotoForQuote: () => void;
}) {
  // In buy mode, the primary action is "add to cart" (batch trade-in flow)
  // and the secondary is "quote only" (walk-up customer asking for a price).
  // In sell mode, both buttons end up at the same view (CardDetail price grid),
  // so we hide the secondary.
  const showQuoteButton = mode === 'buy';
  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-background px-6 text-center">
      <div className="mb-6 rounded-full bg-card p-6">
        <Camera className="h-16 w-16 text-primary" />
      </div>
      <h2 className="mb-2 text-lg font-semibold text-foreground">Scan a card</h2>
      <p className="mb-2 max-w-xs text-sm text-muted-foreground">
        Take a sharp photo of the card face. Make sure the collector number and
        set symbol at the bottom are readable.
      </p>
      <p className="mb-8 text-xs text-muted-foreground">
        {mode === 'buy'
          ? 'Buy mode — add to cart for batch trade-in, or just quote a price.'
          : 'Sell mode — opens the full price detail grid.'}
      </p>
      <div className="flex w-full max-w-xs flex-col gap-3">
        <Button onClick={onTakePhotoForAdd}>
          <Camera className="h-5 w-5" />
          {mode === 'buy' ? 'Take photo · add to cart' : 'Take photo'}
        </Button>
        {showQuoteButton && (
          <Button onClick={onTakePhotoForQuote} variant="secondary">
            <Camera className="h-5 w-5" />
            Quote price · no cart
          </Button>
        )}
      </div>
    </div>
  );
}

function ProcessingView() {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-background">
      <div className="h-12 w-12 animate-spin rounded-full border-2 border-outline border-t-primary" />
      <p className="mt-4 text-sm text-muted-foreground">Identifying card…</p>
    </div>
  );
}

function PickerView({
  candidates,
  onPick,
}: {
  candidates: Card[];
  onPick: (card: Card) => void;
}) {
  const isSingle = candidates.length === 1;
  return (
    <div className="flex h-full w-full flex-col bg-background">
      <div className="flex-1 overflow-auto px-4 pb-4 pt-20">
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          {isSingle ? 'Confirm match' : 'Which card is this?'}
        </h2>
        <div className={cn('grid gap-3', isSingle ? 'mx-auto max-w-sm grid-cols-1' : 'grid-cols-3')}>
          {candidates.map((c) => (
            <button
              key={c.id}
              onClick={() => onPick(c)}
              className="flex flex-col items-start gap-2 rounded-lg bg-card p-3 text-left transition-colors hover:bg-muted"
            >
              {c.image_url ? (
                <CardImage src={c.image_url} alt={c.name} className="w-full rounded-md" />
              ) : (
                <div className="aspect-[3/4] w-full rounded-md bg-muted" />
              )}
              <span className="w-full truncate text-sm font-medium text-card-foreground">
                {c.name}
              </span>
              <span className="w-full truncate font-mono text-xs text-muted-foreground">
                {c.set_name} · #{c.card_number}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmptyView({
  fallbackQuery,
  onFallback,
  onRetry,
  onTakePhoto,
}: {
  fallbackQuery: string | null;
  onFallback: () => void;
  onRetry: () => void;
  onTakePhoto: () => void;
}) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-background px-6 text-center">
      <div className="mb-4 rounded-full bg-muted p-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
      </div>
      <p className="mb-2 text-foreground">No card detected</p>
      <p className="mb-6 text-sm text-muted-foreground">
        {fallbackQuery
          ? 'Try searching manually, or take another photo with better lighting.'
          : 'Make sure the card is well-lit and the collector number is readable.'}
      </p>
      <div className="flex w-full max-w-xs flex-col gap-3">
        {fallbackQuery && (
          <Button onClick={onFallback}>
            <Search className="h-4 w-4" />
            Search for “{fallbackQuery}”
          </Button>
        )}
        <Button onClick={onTakePhoto} variant="secondary">
          <Camera className="h-4 w-4" />
          Take another photo
        </Button>
        <button
          onClick={onRetry}
          className="flex items-center justify-center gap-2 rounded-full bg-transparent px-4 py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <RefreshCw className="h-3 w-3" />
          Reset
        </button>
      </div>
    </div>
  );
}

function ErrorView({
  message,
  onRetry,
  onTakePhoto,
}: {
  message: string | null;
  onRetry: () => void;
  onTakePhoto: () => void;
}) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-background px-6 text-center">
      <div className="mb-4 rounded-full bg-destructive/12 p-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
      </div>
      <p className="mb-6 max-w-xs text-sm text-foreground">
        {message ?? 'Something went wrong.'}
      </p>
      <div className="flex w-full max-w-xs flex-col gap-3">
        <Button onClick={onTakePhoto}>
          <Camera className="h-4 w-4" />
          Try again
        </Button>
        <button
          onClick={onRetry}
          className="flex items-center justify-center gap-2 rounded-full bg-transparent px-4 py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <RefreshCw className="h-3 w-3" />
          Reset
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm sheet (buy mode fast-add)
// ---------------------------------------------------------------------------

interface ConfirmSheetProps {
  card: Card;
  mode: Mode;
  buyPercent: number;
  selectedCondition: keyof PriceConditions;
  onConditionChange: (c: keyof PriceConditions) => void;
  selectedEdition: Edition | null;
  onEditionChange: (e: Edition) => void;
  onAdd: () => void;
}

// Visually a bottom sheet, but not routed through the Sheet primitive: unlike
// CardDetail/CartDrawer, this has no close/cancel affordance of its own — the
// only way out is the parent CameraCapture's persistent top-right close
// button, always rendered above every phase view. A controlled Sheet would
// need an onOpenChange with nothing real to call.
function ConfirmSheet({
  card,
  mode,
  buyPercent,
  selectedCondition,
  onConditionChange,
  selectedEdition,
  onEditionChange,
  onAdd,
}: ConfirmSheetProps) {
  const needsEdition =
    !!card.has_editions && !!card.available_editions && card.available_editions.length > 0;
  // Before an edition is picked on a vintage card, prices would be misleading —
  // don't show condition pills until edition is confirmed.
  const prices =
    needsEdition && !selectedEdition ? undefined : getActivePrices(card, selectedEdition);
  const availableConditions = CONDITION_PILLS.filter((p) => prices?.[p.key] != null);
  const selectedPrice = prices?.[selectedCondition];
  // Cards with NO price data at all (Japanese-only prints, very new releases
  // not yet on TCGplayer) can still be added at $0 so the operator can set
  // the offer in the cart drawer. Vintage edition is still required if applicable.
  const noPricesAvailable =
    !needsEdition && (!card.prices || Object.keys(card.prices).filter((k) => (card.prices as Record<string, number | undefined>)[k] != null).length === 0);
  const canAdd =
    (selectedPrice != null || noPricesAvailable) && (!needsEdition || selectedEdition !== null);
  const displayPrice =
    selectedPrice != null
      ? mode === 'buy'
        ? calculateBuyPrice(selectedPrice, buyPercent)
        : selectedPrice
      : null;

  return (
    <div className="flex h-full w-full items-end justify-center bg-foreground/40">
      <div className="relative w-full max-w-lg animate-slide-up rounded-t-[28px] bg-card pb-safe">
        <div className="flex justify-center py-2">
          <div className="h-1 w-10 rounded-full bg-outline" />
        </div>

        <div className="max-h-[85vh] overflow-auto px-4 pb-6">
          {/* Card header */}
          <div className="flex gap-4">
            <div className="w-24 flex-shrink-0 overflow-hidden rounded-lg">
              {card.image_url ? (
                <CardImage src={card.image_url} alt={card.name} className="w-full rounded-lg" />
              ) : (
                <div className="aspect-[3/4] w-full rounded-lg bg-muted" />
              )}
            </div>
            <div className="flex-1 pt-1">
              <h2 className="text-base font-semibold text-card-foreground">{card.name}</h2>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                {card.set_name} · #{card.card_number}
              </p>
              {card.rarity && (
                <span className="mt-2 inline-block rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {card.rarity}
                </span>
              )}
            </div>
          </div>

          {/* Edition picker (vintage only, mandatory) */}
          {needsEdition && card.available_editions && (
            <div className="mt-4">
              <h3 className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                Edition · tap to confirm
              </h3>
              <div className="flex gap-2">
                {card.available_editions.map((ed) => (
                  <button
                    key={ed}
                    onClick={() => onEditionChange(ed)}
                    className={cn(
                      'flex-1 rounded-full border-2 px-3 py-2 text-sm font-medium transition-all',
                      selectedEdition === ed
                        ? ed === '1st'
                          ? 'border-amber-500 bg-amber-500/20 text-amber-600'
                          : ed === 'shadowless'
                            ? 'border-slate-400 bg-slate-400/20 text-slate-600'
                            : 'border-secondary bg-secondary text-secondary-foreground'
                        : 'border-transparent bg-muted text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {getEditionFullName(ed)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Condition pills (hidden until edition picked on vintage cards) */}
          {prices && availableConditions.length > 0 && (
            <div className="mt-4">
              <h3 className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                Condition
              </h3>
              <div className="grid grid-cols-5 gap-2">
                {availableConditions.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => onConditionChange(c.key)}
                    className={cn(
                      'rounded-lg px-2 py-2 text-sm font-semibold transition-all',
                      selectedCondition === c.key
                        ? mode === 'buy'
                          ? 'bg-muted text-foreground ring-2 ring-tertiary'
                          : 'bg-muted text-foreground ring-2 ring-primary'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Hint when waiting on edition */}
          {needsEdition && !selectedEdition && (
            <p className="mt-4 text-center text-sm text-muted-foreground">
              Pick an edition to see prices.
            </p>
          )}

          {/* Hint when no market price is available (Japanese cards, etc.) */}
          {noPricesAvailable && (
            <p className="mt-4 text-center text-xs text-yellow-700">
              No market price available. Add at $0 and set the offer in the cart.
            </p>
          )}

          {/* Mode label */}
          <div className="mt-4 text-center text-xs text-muted-foreground">
            {mode === 'buy' ? `Buy offer @ ${buyPercent}%` : 'Sell price'}
          </div>

          {/* Primary action */}
          <Button
            onClick={onAdd}
            disabled={!canAdd}
            variant={mode === 'buy' ? 'tertiary' : 'default'}
            className="mt-4 w-full"
          >
            {mode === 'buy' ? 'Add to cart' : 'View details'}
            {displayPrice != null && ` — ${formatPrice(displayPrice)}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Resize a captured photo (File from <input>) to JPEG q85 with max side
 * RESIZE_MAX_SIDE px. Returns a Blob ready for FormData upload.
 *
 * EXIF orientation: Safari 13.4+ and Chrome 81+ auto-orient images drawn from
 * `<img>` to canvas, so portrait iPhone photos come out the right way up.
 */
async function resizeImageFile(file: File, canvas: HTMLCanvasElement): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Failed to load image'));
      i.src = url;
    });

    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    if (!iw || !ih) throw new Error('Image has no dimensions');

    let cw = iw;
    let ch = ih;
    const longest = Math.max(iw, ih);
    if (longest > RESIZE_MAX_SIDE) {
      const scale = RESIZE_MAX_SIDE / longest;
      cw = Math.round(iw * scale);
      ch = Math.round(ih * scale);
    }

    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.drawImage(img, 0, 0, cw, ch);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob returned null'))),
        'image/jpeg',
        JPEG_QUALITY
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function defaultConditionForCard(card: Card): keyof PriceConditions {
  const prices = card.prices;
  if (prices?.nm != null) return 'nm';
  const fallback = (['lp', 'mp', 'hp', 'dm'] as const).find((k) => prices?.[k] != null);
  return fallback ?? 'nm';
}

function describeScanError(err: unknown): string {
  if (err instanceof TimeoutError) {
    return 'Scan timed out. Please try again.';
  }
  if (err instanceof ApiError) {
    if (err.status === 429) return 'Too many scans. Wait a moment before trying again.';
    if (err.status === 401) return 'Session expired. Please log out and reconnect.';
    if (err.status === 413) return 'Image too large. Try again.';
    if (err.status === 422) return 'Unsupported image type.';
    return err.message || `Scan failed (${err.status}). Try again.`;
  }
  // Surface the underlying error so it's visible on-screen without a debugger.
  if (err instanceof Error) {
    return `Scan failed: ${err.name}: ${err.message}`;
  }
  return 'Scan failed. Try again.';
}
